/** @type {import('tailwindcss').Config} */

import { extendTailwindMerge } from "tailwind-merge";
import tailwindAnimatePlugin from "tailwindcss-animate";
import plugin from "tailwindcss/plugin";
import { Breakpoints } from "./src/foundations/breakpoints/breakpoints";
import { ColorPrimitives } from "./src/foundations/color/color-primitives";
import { ElevationPrimitives } from "./src/foundations/elevation/elevation-primitives";
import { RadiiPrimitives } from "./src/foundations/radii/radii-primitives";
import { SpacingPrimitives } from "./src/foundations/spacing/spacing-primitives";
import { SkipInkClasses } from "./src/foundations/typography/skip-ink-classes";
import { TypographyClasses } from "./src/foundations/typography/typography-classes";
import { TypographyPrimitives } from "./src/foundations/typography/typography-primitives";

export default {
  darkMode: ["class"],
  prefix: "tw-",
  content: ["./src/**/*.{html,js,jsx,ts,tsx}"],
  theme: {
    screens: Object.keys(Breakpoints).reduce((breakpoints, size) => {
      breakpoints[size.toLowerCase()] = `${Breakpoints[size]}px`;
      return breakpoints;
    }, {}),
    colors: ({ colors }) => ({
      ...ColorPrimitives,
      transparent: colors.transparent,
      current: colors.current,
      inherit: colors.inherit,
    }),
    borderRadius: RadiiPrimitives,
    spacing: SpacingPrimitives,
    boxShadow: ElevationPrimitives,
    extend: {
      ...TypographyPrimitives,
      animation: {
        spin: "spin 0.75s linear infinite",
        "accordion-down": "accordion-down 0.2s ease-out",
        "accordion-up": "accordion-up 0.2s ease-out",
        "collapsible-down": "collapsible-down 0.2s ease-out",
        "collapsible-up": "collapsible-up 0.2s ease-out",
      },
      zIndex: {
        max: 9999,
      },
      keyframes: {
        "accordion-down": {
          from: {
            height: "0",
          },
          to: {
            height: "var(--radix-accordion-content-height)",
          },
        },
        "accordion-up": {
          from: {
            height: "var(--radix-accordion-content-height)",
          },
          to: {
            height: "0",
          },
        },
        "collapsible-down": {
          from: {
            height: "0",
          },
          to: {
            height: "var(--radix-collapsible-content-height)",
          },
        },
        "collapsible-up": {
          from: {
            height: "var(--radix-collapsible-content-height)",
          },
          to: {
            height: "0",
          },
        },
      },
    },
  },
  safelist: [
    // @TODO set up tailwind in web-2 and remove these
    { pattern: /tw-items-./ },
    { pattern: /tw-ml-./ },
    // @TODO only in dev mode
    ...Object.keys(ElevationPrimitives).map((key) => `tw-${key}`),
    ...Object.keys(RadiiPrimitives).map((key) => `tw-rounded-${key}`),
    ...Object.keys(TypographyClasses).flatMap((key) => {
      const className = `tw-${key.replace(".", "")}`;
      return [
        className,
        ...Object.keys(Breakpoints).map(
          (bp) => `${bp.toLowerCase()}:${className}`,
        ),
      ];
    }),
    ...Object.keys(SpacingPrimitives).map((key) => `tw-size-${key}`),
    ...Object.keys(SkipInkClasses).map((key) => `tw-${key}`),
  ],
  plugins: [
    tailwindAnimatePlugin,
    plugin(function ({ addUtilities }) {
      addUtilities(TypographyClasses);
      addUtilities(SkipInkClasses);
    }),
  ],
  // corePlugins: {
  //   preflight: false
  // }
};

export const twMerge = extendTailwindMerge({
  prefix: "tw-",
  extend: {
    classGroups: {
      typography: Object.keys(TypographyClasses).map(
        (key) => `${key.replace(".", "")}`,
      ),
      skipInk: Object.keys(SkipInkClasses).map(
        (key) => `${key.replace(".", "")}`,
      ),
    },
  },
});
